/**
 * Smart Objects, smart filters and Targeted Curves (Layer menu, Layers panel
 * smart-filter list, High Pass dialog, adjustment preview).
 *
 * The smart-filter commands rebuild exactly the filter list the panel builds
 * and hand it to the same `SetSmartFiltersCommand` with the same label, so
 * the panel runs them unchanged. Rasterizing needs the tab's renderer: the
 * agent API lends it per session (`provideSessionRenderer`); the command
 * renders synchronously inside its transaction, as the menu command does
 * inside its single history entry.
 */
import type { LayerId } from '@maipo/agent-protocol';
import type { CommandExecution } from '../../core/commands';
import type { DocumentSession } from '../../core/session';
import { createId, findLayer, findLayerParent, type EditorDocument, type GroupLayer, type Layer } from '../../core/model';
import { ConvertToSmartObjectCommand, isSmartObject, RasterizeSmartObjectCommand } from '../../core/smart-objects';
import { createHighPassSmartFilter, createToneSmartFilter, SetSmartFiltersCommand, smartFilters, type SmartFilter } from '../../core/smart-filters';
import { SetTargetedCurvePointCommand, type CurveChannel } from '../../core/targeted-adjustment';
import type { Renderer } from '../../render/renderer';
import type { BakedSmartObject } from '../../render/rasterize-smart-object';
import { IntentRejection, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { ancestors, checkContainerUnlocked, checkUnlocked, error, layerFor, parentKey, requireLayer, staticIds } from '../intents/common';
import { defineCommand } from './registry';

type SmartRenderer = Pick<Renderer, 'flatten' | 'disposeDocument'>;
const renderers = new WeakMap<DocumentSession, () => SmartRenderer>();

/** Lends a session the renderer that rasterizing a Smart Object draws with (the export renderer). */
export function provideSessionRenderer(session: DocumentSession, renderer: () => SmartRenderer): void {
  renderers.set(session, renderer);
}

function single(ref: string, aliases: ReadonlyMap<string, LayerId>): Footprint {
  const ids = staticIds([ref], aliases);
  return { reads: ids, writes: ids, structural: false, document: false };
}

/** The Smart Object `ref` names, or diagnostics saying why not. */
function smartObjectFor(document: EditorDocument, ref: string, context: ValidateContext, diagnostics: Diagnostic[]): GroupLayer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (!isSmartObject(layer)) { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not a Smart Object.`, layer.id)); return null; }
  checkUnlocked(document, layer, 'any', diagnostics, context.policy);
  return layer;
}

function requireSmartObject(document: EditorDocument, ref: string, aliases: ReadonlyMap<string, LayerId>): GroupLayer {
  const layer = requireLayer(document, ref, aliases);
  if (!isSmartObject(layer)) throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is not a Smart Object.`, layer.id);
  return layer;
}

// ---------------------------------------------------------------------------
// Smart Objects

export const convertSmartObject = defineCommand<{ layers: string[] }>({
  id: 'smartObject.convert',
  group: 'layers',
  backs: ['action.convert-smart-object'],
  label: () => 'Convert to Smart Object',
  footprint: (document, params, aliases) => {
    const ids = staticIds(params.layers, aliases);
    const parents = [...new Set(ids.filter((id) => findLayer(document.root, id)).map((id) => parentKey(document, id)))];
    return { reads: [...ids, ...parents], writes: [...ids, ...parents], structural: true, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layers = params.layers.map((ref) => layerFor(document, ref, context, diagnostics)).filter((layer): layer is Layer => Boolean(layer));
    const parents = new Set(layers.map((layer) => findLayerParent(document.root, layer.id)?.parent));
    if (parents.size > 1) diagnostics.push(error('COMMAND_REJECTED', 'Smart Object sources must be sibling layers.'));
    const nested = layers.find((layer) => isSmartObject(layer));
    if (nested) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${nested.id} is already a Smart Object; nested Smart Objects are not available.`, nested.id));
    for (const layer of layers) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    const [parent] = parents;
    if (parent && layers[0]) checkContainerUnlocked(document, parent, diagnostics, layers[0].id, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => [new ConvertToSmartObjectCommand([...new Set(params.layers.map((ref) => requireLayer(document, ref, aliases).id))])],
});

export const rasterizeSmartObject = defineCommand<{ layer: string }>({
  id: 'smartObject.rasterize',
  group: 'layers',
  backs: ['action.rasterize-smart-object'],
  asynchronous: true,
  label: () => 'Rasterize Smart Object',
  footprint: (document, params, aliases) => {
    const ids = staticIds([params.layer], aliases);
    const parents = ids.filter((id) => findLayer(document.root, id)).map((id) => parentKey(document, id));
    return { reads: [...ids, ...parents], writes: [...ids, ...parents], structural: true, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = smartObjectFor(document, params.layer, context, diagnostics);
    // The menu command refuses a locked Smart Object or locked parent group for everyone.
    if (layer && context.policy.locks === 'native' && (layer.locked || ancestors(document, layer.id).some((group) => group.locked))) {
      diagnostics.push(error('LAYER_LOCKED', 'Unlock the Smart Object and its parent groups before rasterizing.', layer.id));
    }
    return diagnostics;
  },
  // Prepare only borrows the renderer and loads the renderer module; the
  // pixels are rendered synchronously in compile, inside the transaction, so
  // they always show the Smart Object exactly as the earlier steps left it
  // (a bake taken before an await could be stale by commit time).
  prepare: async (session) => {
    const renderer = renderers.get(session);
    if (!renderer) throw new IntentRejection('COMMAND_REJECTED', 'Rasterizing needs the editor tab\'s renderer, which this session does not have.');
    const { bakeSmartObject } = await import('../../render/rasterize-smart-object');
    const prepared: RasterizePrepared = { live: session.document, renderer: renderer(), bake: bakeSmartObject };
    return prepared;
  },
  compile: (document, params, prepared, aliases) => {
    const value = prepared as RasterizePrepared | undefined;
    const layer = requireSmartObject(document, params.layer, aliases);
    if (!value) throw new IntentRejection('COMMAND_REJECTED', 'Rasterizing needs the editor tab\'s renderer, which this session does not have.', layer.id);
    // A dry run executes on a clone without native resources: it shows the
    // pixels of the live Smart Object as it was before this transaction.
    const source = document === value.live ? document : value.live;
    if (!findLayer(source.root, layer.id)) throw new IntentRejection('COMMAND_REJECTED', `Smart Object ${layer.id} does not exist yet; a dry run cannot render it.`, layer.id);
    let baked: CommandExecution | BakedSmartObject;
    try { baked = value.bake(source, layer.id, value.renderer); } catch (caught) {
      throw new IntentRejection('COMMAND_REJECTED', caught instanceof Error ? caught.message : 'Rendering failed.', layer.id);
    }
    if ('outcome' in baked) throw new IntentRejection(baked.outcome === 'rejected' && /unlock/i.test(baked.reason) ? 'LAYER_LOCKED' : 'COMMAND_REJECTED', baked.outcome === 'rejected' ? baked.reason : 'Nothing to rasterize.', layer.id);
    return [new RasterizeSmartObjectCommand(layer.id, baked.raster)];
  },
});

interface RasterizePrepared {
  live: EditorDocument;
  renderer: SmartRenderer;
  bake: typeof import('../../render/rasterize-smart-object').bakeSmartObject;
}

// ---------------------------------------------------------------------------
// Smart filters: each command rebuilds the list exactly as the panel does.

interface FilterParams { layer: string; filter: string }

function filterIndex(filters: readonly SmartFilter[], id: string, layer: LayerId): number {
  const index = filters.findIndex((filter) => filter.id === id);
  if (index < 0) throw new IntentRejection('COMMAND_REJECTED', `Smart filter ${JSON.stringify(id).slice(0, 80)} does not exist on ${layer}.`, layer);
  return index;
}

/** The native command keeps at most this many filters and would silently drop the rest. */
const MAX_SMART_FILTERS = 32;

function checkFilterRoom(layer: GroupLayer, diagnostics: Diagnostic[]): void {
  if (smartFilters(layer).length >= MAX_SMART_FILTERS) diagnostics.push(error('LIMIT_EXCEEDED', `Smart Object ${layer.id} already has ${MAX_SMART_FILTERS} smart filters.`, layer.id));
}

function validateFilter(document: EditorDocument, params: FilterParams, context: ValidateContext): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const layer = smartObjectFor(document, params.layer, context, diagnostics);
  if (layer && !smartFilters(layer).some((filter) => filter.id === params.filter)) {
    diagnostics.push(error('COMMAND_REJECTED', `Smart filter ${JSON.stringify(params.filter).slice(0, 80)} does not exist on ${layer.id}.`, layer.id));
  }
  return diagnostics;
}

export interface AddFilterParams { layer: string; filter: 'tone' | 'high-pass'; name?: string; enabled?: boolean; settings?: Partial<{ brightness: number; contrast: number; saturation: number; invert: boolean }>; radius?: number }

export const addSmartFilter = defineCommand<AddFilterParams>({
  id: 'smartFilter.add',
  group: 'filters',
  backs: ['panel.layers.smart-filter-add-tone', 'panel.layers.smart-filter-add-high-pass', 'dialog.high-pass.smart'],
  label: (params) => params.filter === 'tone' ? 'Add Smart Filter' : 'Add High Pass Smart Filter',
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = smartObjectFor(document, params.layer, context, diagnostics);
    if (layer) checkFilterRoom(layer, diagnostics);
    if (params.filter === 'tone' && params.radius !== undefined) diagnostics.push(error('COMMAND_REJECTED', 'radius applies to high-pass filters only.'));
    if (params.filter === 'high-pass' && params.settings !== undefined) diagnostics.push(error('COMMAND_REJECTED', 'settings apply to tone filters only.'));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireSmartObject(document, params.layer, aliases);
    let filter: SmartFilter;
    if (params.filter === 'tone') {
      const tone = createToneSmartFilter(params.name);
      if (params.settings) tone.settings = { ...tone.settings, ...params.settings };
      filter = tone;
    } else {
      filter = createHighPassSmartFilter(params.radius);
      if (params.name !== undefined) filter.name = params.name.trim().slice(0, 100) || filter.name;
    }
    if (params.enabled !== undefined) filter.enabled = params.enabled;
    return [new SetSmartFiltersCommand(layer.id, [...smartFilters(layer), filter], params.filter === 'tone' ? 'Add Smart Filter' : 'Add High Pass Smart Filter')];
  },
});

export interface UpdateFilterParams extends FilterParams {
  enabled?: boolean;
  name?: string;
  settings?: Partial<{ brightness: number; contrast: number; saturation: number; invert: boolean }>;
  radius?: number;
  displace?: Partial<{ horizontalScale: number; verticalScale: number; mode: 'fit' | 'tile' }>;
}

function updateLabel(params: UpdateFilterParams, filter: SmartFilter | undefined): string {
  const edits = (['name', 'settings', 'radius', 'displace'] as const).filter((key) => params[key] !== undefined);
  if (edits.length === 0) return 'Toggle Smart Filter';
  if (edits.length === 1 && edits[0] === 'name') return 'Rename Smart Filter';
  return filter?.type === 'high-pass' ? 'Edit High Pass Smart Filter' : filter?.type === 'displace' ? 'Edit Displace Smart Filter' : 'Edit Smart Filter';
}

export const updateSmartFilter = defineCommand<UpdateFilterParams>({
  id: 'smartFilter.update',
  group: 'filters',
  backs: ['panel.layers.smart-filter-toggle', 'panel.layers.smart-filter-edit-tone', 'panel.layers.smart-filter-edit-high-pass', 'panel.layers.smart-filter-edit-displace'],
  label: (params, document) => {
    const layer = findLayer(document.root, params.layer);
    return updateLabel(params, smartFilters(layer).find((filter) => filter.id === params.filter));
  },
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics = validateFilter(document, params, context);
    const layer = findLayer(document.root, params.layer);
    const filter = smartFilters(layer).find((candidate) => candidate.id === params.filter);
    if (filter) {
      if (params.settings !== undefined && filter.type !== 'tone') diagnostics.push(error('COMMAND_REJECTED', `settings apply to tone filters; ${filter.id} is ${filter.type}.`));
      if (params.radius !== undefined && filter.type !== 'high-pass') diagnostics.push(error('COMMAND_REJECTED', `radius applies to high-pass filters; ${filter.id} is ${filter.type}.`));
      if (params.displace !== undefined && filter.type !== 'displace') diagnostics.push(error('COMMAND_REJECTED', `displace applies to displace filters; ${filter.id} is ${filter.type}.`));
    }
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireSmartObject(document, params.layer, aliases);
    const filters = smartFilters(layer);
    const index = filterIndex(filters, params.filter, layer.id);
    const current = filters[index]!;
    let next: SmartFilter = { ...current };
    if (params.enabled !== undefined) next.enabled = params.enabled;
    if (params.name !== undefined) next.name = params.name.trim().slice(0, 100) || current.name;
    if (params.settings !== undefined && next.type === 'tone') next = { ...next, settings: { ...next.settings, ...params.settings } };
    if (params.radius !== undefined && next.type === 'high-pass') next = { ...next, radius: params.radius };
    if (params.displace !== undefined && next.type === 'displace') next = { ...next, settings: { ...next.settings, ...params.displace } };
    return [new SetSmartFiltersCommand(layer.id, filters.map((filter, position) => position === index ? next : filter), updateLabel(params, current))];
  },
});

export const reorderSmartFilter = defineCommand<FilterParams & { index: number }>({
  id: 'smartFilter.reorder',
  group: 'filters',
  backs: ['panel.layers.smart-filter-move-up', 'panel.layers.smart-filter-move-down'],
  label: () => 'Reorder Smart Filters',
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics = validateFilter(document, params, context);
    const count = smartFilters(findLayer(document.root, params.layer)).length;
    if (count > 0 && params.index >= count) diagnostics.push(error('COMMAND_REJECTED', `index must be below ${count}.`));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireSmartObject(document, params.layer, aliases);
    const filters = smartFilters(layer);
    const index = filterIndex(filters, params.filter, layer.id);
    const next = [...filters];
    const [moved] = next.splice(index, 1);
    next.splice(Math.min(params.index, next.length), 0, moved!);
    return [new SetSmartFiltersCommand(layer.id, next, 'Reorder Smart Filters')];
  },
});

export const duplicateSmartFilter = defineCommand<FilterParams>({
  id: 'smartFilter.duplicate',
  group: 'filters',
  backs: ['panel.layers.smart-filter-duplicate'],
  label: () => 'Duplicate Smart Filter',
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics = validateFilter(document, params, context);
    const layer = findLayer(document.root, params.layer);
    if (isSmartObject(layer)) checkFilterRoom(layer, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireSmartObject(document, params.layer, aliases);
    const filters = smartFilters(layer);
    const index = filterIndex(filters, params.filter, layer.id);
    const filter = filters[index]!;
    // Exactly the panel's Duplicate button.
    const duplicate = filter.type === 'tone' ? createToneSmartFilter(`${filter.name} copy`) : filter.type === 'high-pass' ? createHighPassSmartFilter(filter.radius) : structuredClone(filter);
    if (duplicate.type === 'tone' && filter.type === 'tone') duplicate.settings = structuredClone(filter.settings);
    else if (duplicate.type === 'displace') {
      duplicate.id = createId('smart-filter');
      duplicate.name = `${filter.name} copy`;
    } else duplicate.name = `${filter.name} copy`;
    return [new SetSmartFiltersCommand(layer.id, [...filters.slice(0, index + 1), duplicate, ...filters.slice(index + 1)], 'Duplicate Smart Filter')];
  },
});

export const deleteSmartFilter = defineCommand<FilterParams>({
  id: 'smartFilter.delete',
  group: 'filters',
  backs: ['panel.layers.smart-filter-delete'],
  label: () => 'Delete Smart Filter',
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => validateFilter(document, params, context),
  compile: (document, params, _prepared, aliases) => {
    const layer = requireSmartObject(document, params.layer, aliases);
    const filters = smartFilters(layer);
    filterIndex(filters, params.filter, layer.id);
    return [new SetSmartFiltersCommand(layer.id, filters.filter((candidate) => candidate.id !== params.filter), 'Delete Smart Filter')];
  },
});

// ---------------------------------------------------------------------------
// Targeted Curves

export const targetedCurve = defineCommand<{ layer: string; channel: CurveChannel; input: number; output: number }>({
  id: 'adjustment.targetedCurve',
  group: 'filters',
  backs: ['panel.layers.adjustment-targeted-curves-drag'],
  label: () => 'Targeted Curves adjustment',
  footprint: (_document, params, aliases) => single(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'adjustment') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not an adjustment.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    if (layer.kind !== 'adjustment') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is not an adjustment.`, layer.id);
    return [new SetTargetedCurvePointCommand(layer.id, params.channel, params.input, params.output)];
  },
});

export const SMART_COMMANDS = [convertSmartObject, rasterizeSmartObject, addSmartFilter, updateSmartFilter, reorderSmartFilter, duplicateSmartFilter, deleteSmartFilter, targetedCurve] as const;
